import common from "../../common";
import Mixture from "./Mixture";
import Whisk from "./Whisk";

export default class MixingBowl {
    constructor(scene) {
        this._front = this._back = this._shadow = this._mixture = null;
        this._whisk = null;

        this._bowlAnimationFlags = [];
        this._animateUp = false;
        this._animateDown = false;
        this._animateWhisk = false;

        this._signals = {};
        this._signals._bowlUpComplete = new P3.utils.MiniSignal();
        this._signals._bowlDownComplete = new P3.utils.MiniSignal();

        this.createBowlSprites(scene);
        this.createWhisk(scene);
    }

    destroy(scene){
        scene.removeChild(this._mixture);
        this._back._floatTween.stop();
        this._front._floatTween.stop();
        this._shadow._floatTween.stop();
        this._mixture._upTween.stop();
        this._mixture._moveUpTween.stop();
        this._mixture._floatTween.stop();
        this._mixture.destroy();
        this._whisk._continousTween.stop();
        scene.removeChild(this._whisk);
    }

    update() {
        this._mixture.update();
        this._whisk.update();

        if(this._animateUp && this.checkAnimationState() && !this._animateWhisk){
            this.animateUp();
            this._animateUp = false;
        }

        if(this._animateDown && this.checkAnimationState()){
            this.animateDown();
            this._animateDown = false;
        }

        if(this._animateWhisk && !this._whisk._animating){
            this._whisk.animateSingle();
            this._animateWhisk = false;
        }
    }

    animateUp(){
        for (let section of this._bowlAnimationFlags){
            section._upTween.start();
        }
        this._whisk.animateUp();

        this._mixture._upTween = this._mixture.animateUp();
        this._mixture._upTween.onComplete(()=> {
            this._mixture._animating = false;
            this._mixture.animateFloat();
            this._signals._bowlUpComplete.dispatch();
            //console.log("mixture float");
        });
        this._mixture._upTween.start();
    }

    animateDown(){
        for (let section of this._bowlAnimationFlags){
            section._floatTween.stop();
            section._downTween.start();
        }

        this._whisk.animateDown();
        this._mixture.animateDown();
    }

    checkAnimationState(){
        for (let section of this._bowlAnimationFlags){
            if(section._animating)
                return false;
        }

        if(this._whisk._animating)
            return false;

        if(this._mixture._animating || this._mixture._animatingMixture)
            return false;

        return true;
    }

    createBowlSprites(scene){
        let newSprite = (texture, position)=>{
            let sprite = new PIXI.Sprite(P3.assets.texture(texture));
            sprite.anchor.set(0.5);
            sprite.scale.set(0.8);
            sprite.position.set(position.x, position.y);
            sprite['_startPosition'] = position;
            sprite['_upPosition'] = {x: position.x, y: position.y - 100};
            sprite['_animating'] = false;
            return sprite;
        };

        let animateUp = (object)=>{
            return new P3.utils.TWEEN.Tween(object)
                .to({
                    y: object._upPosition.y
                }, 2000)
                .easing(P3.utils.Easing.Back2.Out)
                .onStart(()=> {
                    object._animating = true;
                })
                .onComplete(()=> {
                    object._animating = false;
                    this._signals._bowlUpComplete.dispatch();
                });
        };
        let animateDown = (object)=>{
            return new P3.utils.TWEEN.Tween(object)
                .to({
                    y: object._startPosition.y
                }, 2000)
                .easing(P3.utils.Easing.Back2.In)
                .onStart(()=> {
                    object._animating = true;
                })
                .onComplete(()=> {
                    object._animating = false;
                    this._signals._bowlDownComplete.dispatch();
                });
        };
        let animateFloat = (object)=>{
            return new P3.utils.TWEEN.Tween(object)
                .to({
                    y: object._startPosition.y - 90
                }, 2000)
                .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                .repeat(Infinity)
                .yoyo(true);
        };

        this._back = newSprite('bowl_back', {
            x: common.STAGE_WIDTH * 0.5,
            y:common.STAGE_HEIGHT * 0.5 + 200
        });
        this._back._upTween = animateUp(this._back);
        this._back._downTween = animateDown(this._back);
        this._back._floatTween = animateFloat(this._back);
        this._back._upTween.chain(this._back._floatTween);
        this._bowlAnimationFlags.push(this._back);

        this._mixture = new Mixture('bowl_content', {
            x: common.STAGE_WIDTH * 0.5,
            y:common.STAGE_HEIGHT * 0.5 + 260
        });
        this._mixture._startPosition = {
            x: common.STAGE_WIDTH * 0.5,
            y:common.STAGE_HEIGHT * 0.5 + 260
        };
        this._mixture._upPosition = {
            x: this._mixture._startPosition.x,
            y:  this._mixture._startPosition.y - 100
        };
        this._mixture._floatTween = animateFloat(this._mixture);
        //this._bowlAnimationFlags.push(this._mixture);

        this._front = newSprite('bowl_front', {
            x: common.STAGE_WIDTH * 0.5,
            y:common.STAGE_HEIGHT * 0.5 + 200
        });
        this._front._upTween = animateUp(this._front);
        this._front._downTween = animateDown(this._front);
        this._front._floatTween = animateFloat(this._front);
        this._front._upTween.chain(this._front._floatTween);
        this._bowlAnimationFlags.push(this._front);

        this._shadow = newSprite('bowl_shadow', {
            x:common.STAGE_WIDTH * 0.5 - 50,
            y:common.STAGE_HEIGHT * 0.5 + 300
        });
        this._shadow._upTween = new P3.utils.TWEEN.Tween(this._shadow.scale)
            .to({
                x: 0.8,
                y: 1.0
            }, 2000)
            .easing(P3.utils.Easing.Back2.Out);

        this._shadow._downTween = new P3.utils.TWEEN.Tween(this._shadow.scale)
            .to({
                x: 0.8,
                y: 0.8
            }, 2000)
            .easing(P3.utils.Easing.Back2.Out);

        this._shadow._floatTween = new P3.utils.TWEEN.Tween(this._shadow.scale)
            .to({
                x: .7,
                y: .9
            }, 2000)
            .repeat(Infinity)
            .yoyo(true)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut);

        this._shadow._upTween.chain(this._shadow._floatTween);
        this._bowlAnimationFlags.push(this._shadow);

        scene.addChildAt(this._back, 2);
        scene.addChildAt(this._mixture, 3);
        scene.addChild(this._shadow);
        scene.addChild(this._front);

        //console.log(this._bowlAnimationFlags);
    }

    createWhisk(scene){
        this._whisk = new Whisk(P3.assets.texture('whisk'), {
            x:common.STAGE_WIDTH * 0.5 - 125,
            y:common.STAGE_HEIGHT * 0.5 + 125
        });
        this._whisk.view.scale.set(0.5);
        this._whisk.rotation = common.deg2rad(-25);

        scene.addChildAt(this._whisk, 4);
    }
}